import React, { Suspense, useEffect, useRef } from "react";
import axiosIntegration from "../Common/Axios/axiosIntegration";
import {
  ApiConfigData,
  ClientPlatformCode,
  DefaultCardName,
  getImageName,
  handleRedirectWithLoading,
  ModalOrSso,
  SectionContentMasterData,
  SectionPersonalizationOverride,
  ShouldShowSection,
  trackEvent,
  UserIdAndStatusData,
  UtilityReducer,
} from "../Utility/Utility";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import API from "../Common/Endpoint/serviceConstrants";
import getTranslation from "../TranslationUtility/TranslationUtility";
import { InView } from "react-intersection-observer";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Loader from "../Utility/Loader/Loader";

const overrideSliderSettings = {
  dots: true,
  arrows: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 3000,
  pauseOnHover: false,
  pauseOnFocus: false,
  adaptiveHeight: true,
};

// Lazy load components
const CardSection = React.lazy(() =>
  import("../Components/CardSection/CardSection")
);
const PromoBanner = React.lazy(() =>
  import("../Components/PromoBanner/PromoBanner")
);
const WelcomeOffer = React.lazy(() =>
  import("../Components/WelcomeOffer")
);
const CustomerMenu = React.lazy(() =>
  import("../Components/CustomerMenu/CustomerMenu")
);
const SupportingBanner = React.lazy(() =>
  import("../Components/SupportingBanner/SupportingBanner")
);
const SpecialSection = React.lazy(() =>
  import("../Components/SpecialSection/SpecialSection")
);
const TopOffer = React.lazy(() => import("../Components/TopOffer/TopOffer"));
const GiftCardSection = React.lazy(() =>
  import("../Components/GiftCardSection/GiftCardSection")
);
const SpecialCartBanner = React.lazy(() =>
  import("../Components/SpecialCartBanner/SpecialCartBanner")
);
const PromoMessage = React.lazy(() =>
  import("../Components/PromoMessage/PromoMessage")
);

const FeaturedOffer = React.lazy(() =>
  import("../Components/FeaturedOffer/FeaturedOffer")
);

const HomePageHeader = React.lazy(() =>
  import("../Components/HomePageHeader/HomePageHeader")
);

// A section-level override block is either raw HTML or a structured
// image/title/description/text/cta object — same content shape used
// throughout personalization, just rendered standalone here rather than as
// a section's own content-list item.
const renderOverrideBlock = (
  block,
  index,
  isMobileView,
  sectionMasterId,
  onAnchorRedirect,
  override,
  onTrackSelectPromotion
) => {
  // Same enabled:false convention as every other content item — a disabled
  // block doesn't render at all, and if every block in an override ends up
  // disabled, renderSectionOverride's own "no blocks left" check already
  // means the whole override doesn't render either.
  if (block?.enabled === false) return null;

  if (block?.htmlContent) {
    // Raw HTML isn't React-managed, so individual anchors inside it can't
    // get their own onClick prop — catch the click on the wrapping div
    // instead and walk up from e.target to find which anchor (if any) was
    // actually hit. A real href would otherwise navigate the browser
    // directly, bypassing the same login/cta gating every other section's
    // links go through, so preventDefault and hand it to onAnchorRedirect
    // instead, same as the content-level htmlContent items.
    return (
      <div
        key={`personalization-block-${index}`}
        onClick={(e) => {
          const anchor = e.target.closest("a");
          if (anchor) e.preventDefault();
          console.log("[Personalization] section override HTML block clicked", {
            sectionMasterId,
            blockIndex: index,
            clickedTag: e.target.tagName,
            clickedText: e.target.textContent?.trim(),
            anchorHref: anchor?.href || null,
            anchorText: anchor?.textContent?.trim() || null,
            anchorName: anchor?.name || null,
          });
          onTrackSelectPromotion?.(block, override, anchor?.href, anchor?.name);
          if (anchor?.href) {
            onAnchorRedirect?.(anchor.href);
          }
        }}
        dangerouslySetInnerHTML={{ __html: block.htmlContent }}
      />
    );
  }

  const imagePath = isMobileView ? (block?.mobImage || block?.webImage) : (block?.webImage || block?.mobImage);
  if (!imagePath && !block?.title && !block?.text && !block?.description) return null;

  const imageSrc = imagePath
    ? (imagePath.includes("http") ? imagePath : `${process.env.REACT_APP_CLIENT_IMAGE_URL}${imagePath}`)
    : "";
  const image = imageSrc ? (
    <img src={imageSrc} alt={block?.title || block?.text || ""} style={{ width: "100%", display: "block" }} />
  ) : null;

  return (
    <div key={`personalization-block-${index}`} className="">
      {block?.cta && image ? (
        <a
          href={block.cta}
          rel="noreferrer"
          onClick={(e) => {
            e.preventDefault();
            console.log("[Personalization] section override block CTA clicked", {
              sectionMasterId,
              blockIndex: index,
              block,
              override
            });
            onTrackSelectPromotion?.(block, override);
            onAnchorRedirect?.(block.cta);
          }}
        >
          {image}
        </a>
      ) : image}
    </div >
  );
};

// fullWidth mirrors the same two layouts every other section already
// renders in (e.g. PromoBanner's isFullWidth): edge-to-edge with no
// container padding, vs. a padded container. Either way, more than one
// block becomes a carousel rather than a stack — same as those sections.
const renderSectionOverride = (
  override,
  isMobileView,
  onAnchorRedirect,
  onTrackViewPromotion,
  onTrackSelectPromotion,
  listKey
) => {
  const blocks = (override?.blocks || [])
    .map((block, index) => {
      const element = renderOverrideBlock(
        block,
        index,
        isMobileView,
        override?.sectionMasterId,
        onAnchorRedirect,
        override,
        onTrackSelectPromotion
      );

      if (!element) return null;

      const trackingKey = `${override?.sectionMasterId || ""}:${override?.position || "top"}:${index}:${block?.id || block?.webImage || block?.mobImage || "block"}`;

      return (
        <InView
          key={`override-inview-${trackingKey}`}
          threshold={0.5}
          onChange={(inView) => {
            if (inView) {
              onTrackViewPromotion?.(block, override, trackingKey);
            }
          }}
        >
          {({ ref }) => <div ref={ref}>{element}</div>}
        </InView>
      );
    })
    .filter(Boolean);
  if (!blocks.length) return null;

  const wrapperClassName = override?.fullWidth ? "w-full" : "all-p px-3 pt-3 container mx-auto";

  return (
    <div key={listKey} data-personalization-section-id={override?.sectionMasterId} className={wrapperClassName}>
      {override?.title ? <div className={`row ${override?.fullWidth ? "ml-4" : ""}`}><div className="home-head heading">{override.title}</div></div> : null}
      {blocks.length > 1 ? <Slider key={`${override?.sectionMasterId}-${blocks.length}`} {...overrideSliderSettings}>{blocks}</Slider> : blocks[0]}
    </div>
  );
};

const GetComponent = ({
  sectionData,
  isMobileView,
  setShowModal,
  unlockData,
  setIsLoading,
  setIsModalOpenoffer,
  isModalOpenoffer,
  setCustomeLoader,
  isMyAccountPage,
}) => {
  const translation = getTranslation();
  const login = sessionStorage.getItem("login");
  const sectionOverride = SectionPersonalizationOverride(sectionData?.id);
  const overrideViewTrackedRef = useRef(new Set());
  const card = UtilityReducer();
  const creditCards = card?.creditCards?.filter(card => card?.status === "1");
  const creditCardsWithExclude = card?.creditCards?.filter(card => card?.status === "1" || card?.status === "2");
  const debitCards = card?.debitCards;
  const { clientCode: gaClientCode, platformCode: gaPlatformCode } = ClientPlatformCode();
  const { userId: gaUserId, userStatus: gaUserStatus } = UserIdAndStatusData();
  const gaDefaultCardName = DefaultCardName();
  const generateToken = useSelector((state) => state.generateTokenSliceReducer);
  const apiconfig = ApiConfigData();
  const { sctionContentdata } = SectionContentMasterData();
  const partnerCode = apiconfig?.find(
    (data) => data?.paramsKey === "partnerCode"
  )?.paramsValue;
  const clientCode = apiconfig?.find(
    (data) => data?.paramsKey === "clientCode"
  )?.paramsValue;

  // Same login/cta gating every other section's links already go through —
  // a section-override anchor shouldn't behave any differently just because
  // it came from raw HTML instead of a structured content field.
  const isModal = ModalOrSso();
  const navigate = useNavigate();
  const integrationId = apiconfig?.find(
    (data) => data?.paramsKey === "integrationId"
  )?.paramsValue;
  const loginUrl = `${process.env.REACT_APP_INTEGRATION_LOGIN_URL}Login/${integrationId}`;
  const handleOverrideAnchorRedirect = (cta) => {
    if (!cta) return;
    handleRedirectWithLoading(setIsLoading, cta, login, setShowModal, isModal, navigate, loginUrl);
  };

  const trackOverridePromotion = (eventName, block, override, anchorHref, anchorName) => {
    let trackData = {
      client_code: gaClientCode,
      platform_code: gaPlatformCode,
      user_id: gaUserId,
      user_status: gaUserStatus,
      card_name: gaDefaultCardName || "NA",
      page_type: window.location.pathname === "/" ? "home_page" : "my_account_page",
      segment_Name: block?.gaEvent?.segmentName,
      campaign_Name: block?.gaEvent?.campaignName,

    }
    if (eventName === "select_personalisation") {
      trackData = {
        ...trackData,
        content_name: block?.gaEvent?.bannerName || anchorName,
        destination_url: block?.cta || anchorHref || "NA",
      }
    };
    trackEvent(eventName, trackData);
  }

  const trackOverrideViewPromotion = (block, override, trackingKey) => {
    if (overrideViewTrackedRef.current.has(trackingKey)) return;
    overrideViewTrackedRef.current.add(trackingKey);

    trackOverridePromotion("view_personalisation", block, override);
  };

  const trackOverrideSelectPromotion = (block, override, anchorHref, anchorName) => {
    trackOverridePromotion("select_personalisation", block, override, anchorHref, anchorName);
  };

  const isSpecialSection = sectionData?.sectionType === "Special Section";
  const filteredData = isSpecialSection
    ? sctionContentdata?.filter(
      (filterData) => filterData.sectionMasterId === sectionData.id
    )
    : [];

  useEffect(() => {
    if (login) {
      setTimeout(() => {
        sessionStorage.setItem("postLoginSuccesCount", 2);
      }, 500);
    }
  }, [login]);

  return (
    //  <InView triggerOnce={true} threshold={0.1}>
    // {({ inView, ref }) => ( 
    <div  //ref={ref}
    >
      {
        // inView?(
        // ...existing code...
        (() => {
          const componentToRender = (() => {
            switch (sectionData?.sectionType) {
              case translation?.promoMessage:
                return (
                  <PromoMessage sectionId={sectionData?.id} login={login} />
                );

              case translation?.customerCards:
                if (ShouldShowSection(sectionData, login, isMobileView)) {
                  return login && creditCards?.length ? (
                    <Suspense
                      fallback={login ? <></> : <Loader />}
                    >
                      <CardSection
                        card={card}
                        debitCards={debitCards}
                        creditCards={creditCards}
                        axiosIntegration={axiosIntegration}
                        generateToken={generateToken}
                        API={API}
                        clientCode={clientCode}
                        partnerCode={partnerCode}
                        sectionId={sectionData?.id}
                      />
                    </Suspense>
                  ) : (
                    ""
                  );
                }
                return null;

              case translation?.promoBanner:
                if (ShouldShowSection(sectionData, login, isMobileView)) {
                  return (
                    <PromoBanner
                      sectionData={sectionData}
                      text={sectionData?.text}
                      sectionId={sectionData?.id}
                      setShowModal={setShowModal}
                      login={login}
                      unlockData={unlockData}
                      creditCards={creditCards}
                      setIsLoading={setIsLoading}
                      isMobileView={isMobileView}
                      isMyAccountPage={isMyAccountPage}
                    />
                  );
                }
                return null;

              case translation?.welcomeOffer:
                if (ShouldShowSection(sectionData, login, isMobileView)) {
                  return (
                    <WelcomeOffer
                      sectionData={sectionData}
                      text={sectionData?.text}
                      sectionId={sectionData?.id}
                      setShowModal={setShowModal}
                      login={login}
                      unlockData={unlockData}
                      creditCards={creditCards}
                      setIsLoading={setIsLoading}
                      isMobileView={isMobileView}
                    />
                  );
                }
                return null;
              case translation?.customerMenu:
                if (ShouldShowSection(sectionData, login, isMobileView)) {
                  return (
                    <CustomerMenu
                      isMobileView={isMobileView}
                      text={sectionData?.text}
                      mobileHeading={sectionData?.mobileHeading}
                      sectionId={sectionData?.id}
                      setShowModal={setShowModal}
                      login={login}
                      setIsLoading={setIsLoading}
                      designLayoutType={sectionData?.designLayoutType}
                      isMyAccountPage={isMyAccountPage}
                    />
                  )
                }
                return null;

              case !sectionData?.isOfferListPage
                ? "Supporting Banner"
                : false:
                if (ShouldShowSection(sectionData, login, isMobileView)) {
                  return (
                    <SupportingBanner
                      isMobileView={isMobileView}
                      text={sectionData?.text}
                      isFullWidth={sectionData?.isFullWidth}
                      sectionId={sectionData?.id}
                      setShowModal={setShowModal}
                      login={login}
                      setIsLoading={setIsLoading}
                      sectionData={sectionData}
                    />
                  );
                }
                return null;

              case translation?.specialSection:
                const hasProducts = filteredData?.some(
                  (item) => item.contentSourceType === translation?.products
                );
                const hasGiftCards = filteredData?.some(
                  (item) => item.contentSourceType === translation?.giftCards
                );

                if (hasProducts) {
                  return (
                    <SpecialSection
                      setShowModal={setShowModal}
                      login={login}
                      sectionData={sectionData}
                      setIsLoading={setIsLoading}
                      isMobileView={isMobileView}
                    />
                  );
                }
                if (!hasProducts && hasGiftCards) {
                  return (
                    <GiftCardSection
                      isMobileView={isMobileView}
                      sectionData={sectionData}
                      setShowModal={setShowModal}
                      login={login}
                      setIsLoading={setIsLoading}
                    />
                  );
                }

                if (
                  filteredData?.some(
                    (item) => item.contentSourceType === "Deals"
                  )
                ) {
                  return (
                    <TopOffer
                      login={login}
                      setShowModal={setShowModal}
                      sectionData={sectionData}
                      setIsModalOpenoffer={setIsModalOpenoffer}
                      isModalOpenoffer={isModalOpenoffer}
                      creditCards={creditCardsWithExclude}
                      isMobileView={isMobileView}
                    />
                  );
                }
                return null;
              case translation?.offerSection || "Offer Section":
                if (ShouldShowSection(sectionData, login, isMobileView)) {
                  return (
                    <FeaturedOffer
                      login={login}
                      setShowModal={setShowModal}
                      sectionData={sectionData}
                      setIsModalOpenoffer={setIsModalOpenoffer}
                      isModalOpenoffer={isModalOpenoffer}
                      creditCards={creditCardsWithExclude}
                      isMobileView={isMobileView}
                      setIsLoading={setIsLoading}
                    />
                  );
                }
                return null;

              case translation?.specialCartBanner:
                return (
                  <SpecialCartBanner
                    setShowModal={setShowModal}
                    login={login}
                    text={sectionData?.text}
                    sectionId={sectionData?.id}
                    setIsLoading={setIsLoading}
                    isMobileView={isMobileView}
                  />
                );
              case translation?.homePageHeader:
                if (ShouldShowSection(sectionData, login, isMobileView) && isMobileView) {
                  return (
                    <HomePageHeader
                      login={login}
                      sectionData={sectionData}
                      card={card}
                      debitCards={debitCards}
                      creditCards={creditCards}
                      axiosIntegration={axiosIntegration}
                      generateToken={generateToken}
                      API={API}
                      clientCode={clientCode}
                      partnerCode={partnerCode}
                      setCustomeLoader={setCustomeLoader}
                      setIsLoading={setIsLoading}
                      setShowModal={setShowModal}
                      isMobileView={isMobileView}
                    />
                  );
                }
                break;
              default:
                return null;
            }
          })();

          // A section that wouldn't render at all here (hidden for this
          // device/journey/login state, or simply has nothing to show) stays
          // hidden — personalization targets a section that's actually
          // visible, it doesn't force one to appear where it's specifically
          // configured not to.
          if (!componentToRender) return componentToRender;

          // Every branch above now returns a bare lazy component — Suspense
          // wraps the combined output ONCE, below, rather than once per
          // branch. That's not just tidier: it's why the override used to
          // render ahead of the section it targets. Suspense only defers
          // the subtree *inside* its own boundary; the override elements
          // (no async dependency of their own) were plain siblings of the
          // per-branch Suspense, so they committed immediately while the
          // section's lazy chunk was still loading. Putting everything
          // inside one boundary means the whole thing — override and base
          // section together — waits for the same chunk before any of it
          // paints.
          const suspenseFallback = login ? <></> : <Loader />;

          if (!sectionOverride?.length) {
            return <Suspense fallback={suspenseFallback}>{componentToRender}</Suspense>;
          }

          // "replace" only swaps out the base render itself — it doesn't
          // suppress other "add" entries targeting different positions on
          // the same section, those still wrap around whatever the base
          // ends up being.
          const replaceEntry = sectionOverride.find((entry) => entry.action === "replace");
          const addEntries = sectionOverride.filter((entry) => entry.action !== "replace");

          let baseElement = componentToRender;
          if (replaceEntry) {
            const replaceElement = renderSectionOverride(
              replaceEntry,
              isMobileView,
              handleOverrideAnchorRedirect,
              trackOverrideViewPromotion,
              trackOverrideSelectPromotion
            );
            if (replaceElement) baseElement = replaceElement;
          }

          if (!addEntries.length) {
            return <Suspense fallback={suspenseFallback}>{baseElement}</Suspense>;
          }

          // Each distinct position is an independent block around the base
          // — only entries that share both position and sectionType were
          // already combined by the engine, so top/bottom/etc. here render
          // side by side (possibly more than one per side), not merged
          // into one. Per spec, a blank position on a section-level add
          // defaults to "above" (before), not below.
          const beforeEntries = addEntries.filter((entry) => entry.position === "top" || entry.position === "left" || !entry.position);
          const afterEntries = addEntries.filter((entry) => entry.position === "bottom" || entry.position === "right");

          const beforeElements = beforeEntries
            .map((entry, index) =>
              renderSectionOverride(
                entry,
                isMobileView,
                handleOverrideAnchorRedirect,
                trackOverrideViewPromotion,
                trackOverrideSelectPromotion,
                `override-before-${index}`
              )
            );
          const afterElements = afterEntries
            .map((entry, index) =>
              renderSectionOverride(
                entry,
                isMobileView,
                handleOverrideAnchorRedirect,
                trackOverrideViewPromotion,
                trackOverrideSelectPromotion,
                `override-after-${index}`
              )
            );

          return (
            <Suspense fallback={suspenseFallback}>
              {beforeElements}
              {baseElement}
              {afterElements}
            </Suspense>
          );
        })()
      }
    </div>
  );
};
export default GetComponent;
